import { useCallback, useRef, useState } from "react";
import {
  type GoogleMarker,
  type PaginationDataType,
  type dtoSalesPartnerData,
} from "~/domains/salesPartner/types/SalesPartnerTypes";
import {
  getSalesPartnerSearch,
  SalesPartnerApiError,
  type UrlParams,
} from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { markersTransformer } from "~/domains/salesPartner/utils/transformers";
import { makersWithPreferredSalesPartner, hasActiveSelectedSubsidiary } from "~/domains/affiliate/utils/utilsAccount";
import { SearchTypesSP } from "~/features/salesPartner/components/SearchCard/SalesPartnerSearchBox";
import {
  SalesPartnerActions,
  SalesPartnerMethods,
  trackPartnerFinderSearch,
  trackSalesPartnerActions,
} from "~/app/analytics/ga/salesPartner";
import { transformMarkerListData } from "~/features/cart/B2CRequestPage/utils";
import type { SASearchFormFields } from "~/features/salesPartner/components/SearchCard/SalesPartnerSearchBox";

interface UseSalesPartnerSearchOptions {
  pageSize: number;
  preferredSalesPartnerId?: string;
  onMarkersLoaded?: (markers: GoogleMarker[], selectFirst: boolean) => void;
  getFormValues?: () => SASearchFormFields;
}

interface SearchResult {
  markers: GoogleMarker[];
  pagination: PaginationDataType;
}

export interface SalesPartnerSearchError {
  status: number;
  message: string;
}

export function useSalesPartnerSearch(options: UseSalesPartnerSearchOptions) {
  const { pageSize, preferredSalesPartnerId, onMarkersLoaded, getFormValues } = options;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [allMarkers, setAllMarkers] = useState<GoogleMarker[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationDataType>({
    currentPage: 0,
    numberOfPages: 2,
    pageSize: pageSize,
    totalNumberOfResults: 0,
  });
  const [error, setError] = useState<SalesPartnerSearchError | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const processResponse = useCallback(
    (response: dtoSalesPartnerData, selectFirstAsPreferred: boolean) => {
      const transformedMarkers = markersTransformer(response);
      const markers = makersWithPreferredSalesPartner(transformedMarkers, preferredSalesPartnerId ?? "");

      setAllMarkers(markers);
      setPaginationData({
        ...response.pagination,
        numberOfPages: Math.ceil(response.pagination.totalNumberOfResults / pageSize),
        pageSize: pageSize,
      });
      setError(null);

      const shouldSelectFirst =
        selectFirstAsPreferred && hasActiveSelectedSubsidiary(markers) && !!preferredSalesPartnerId;
      onMarkersLoaded?.(markers, shouldSelectFirst);

      return { markers, pagination: response.pagination };
    },
    [pageSize, preferredSalesPartnerId, onMarkersLoaded],
  );

  /**
   * Search for sales partners with the given parameters.
   * This is the main search function used for user-initiated searches.
   */
  const search = useCallback(
    async (
      params: UrlParams,
      pageNumber: number = 0,
      trackingMethod?: SalesPartnerMethods,
    ): Promise<SearchResult | null> => {
      setIsLoading(true);
      setError(null);

      // Abort any in-flight request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await getSalesPartnerSearch(params, pageNumber, controller.signal);
        const result = processResponse(response, true);

        // Track analytics
        const method =
          trackingMethod ??
          (params.searchType === SearchTypesSP.Partner ? SalesPartnerMethods.NAME : SalesPartnerMethods.ADDRESS);

        if (getFormValues) {
          trackPartnerFinderSearch(getFormValues(), "Finding Performed", "Click");
        }
        trackSalesPartnerActions(
          SalesPartnerActions.SEARCH,
          method,
          undefined,
          transformMarkerListData(result.markers),
        );

        setIsLoading(false);
        return result;
      } catch (e: unknown) {
        // AbortError is expected when a new search is initiated
        if (e instanceof DOMException && e.name === "AbortError") {
          return null;
        }

        // Handle API errors with proper status code
        if (e instanceof SalesPartnerApiError) {
          setError({
            status: e.status,
            message: e.message,
          });
        } else if (e instanceof Error) {
          setError({
            status: 500,
            message: e.message || "An error occurred while searching for sales partners",
          });
        }

        setIsLoading(false);
        return null;
      }
    },
    [processResponse, getFormValues],
  );

  /**
   * Search for sales partners on initial mount (automatic geolocation-based search).
   * This does not track user-initiated analytics.
   */
  const searchOnMount = useCallback(
    async (params: UrlParams): Promise<SearchResult | null> => {
      // Abort any in-flight request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await getSalesPartnerSearch(params, 0, controller.signal);
        const result = processResponse(response, false);

        // Track as automatic search (geolocation-based)
        trackSalesPartnerActions(
          SalesPartnerActions.SEARCH,
          SalesPartnerMethods.AUTOMATIC,
          undefined,
          transformMarkerListData(result.markers),
        );

        setIsLoading(false);
        return result;
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === "AbortError") {
          return null;
        }

        // Handle API errors with proper status code
        if (e instanceof SalesPartnerApiError) {
          setError({
            status: e.status,
            message: e.message,
          });
        } else if (e instanceof Error) {
          setError({
            status: 500,
            message: e.message || "An error occurred while searching for sales partners",
          });
        }

        setIsLoading(false);
        return null;
      }
    },
    [processResponse],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    allMarkers,
    paginationData,
    error,
    search,
    searchOnMount,
    clearError,
    setPaginationData,
  };
}
